import { useState, type CSSProperties } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import type { LeaveStatus, LeaveTypeId } from '@lms/shared-types'
import { Badge } from '../primitives/Badge'
import { Icon } from '../primitives/Icon'
import { AnimatedHeight } from '../primitives/AnimatedHeight'
import { LeaveDayBreakdown } from './LeaveDayBreakdown'
import {
  LEAVE_TYPE_ACCENT,
  LEAVE_TYPE_HALF_DAY_ALLOWED,
  LEAVE_TYPE_LABEL,
  STATUS_META,
  ownerFacingStatus,
} from '../../lib/leave-type-meta'

/**
 * Compact "upcoming leave" list for the employee home. Answers the most common
 * employee question directly — "what have I got booked, and what's its status?"
 * — in a scannable, action-per-row form, where the two-month planning calendar
 * (freeze bands, holidays) is the wrong tool. See the calendar-vs-list tradeoff
 * discussion: the calendar stays for planning, this list carries the status job.
 *
 * Input is already scoped to the viewer's own active (plannable) leaves — see
 * `isHomeCalendarLeave`. We further keep only leaves that haven't fully ended.
 */

/**
 * The minimal leave shape this list needs. Deliberately narrower than
 * `CalendarLeave` (no team/employee-name fields) so it can be fed from either
 * the calendar payload OR the full `/me/leaves` list — the latter is what the
 * home card uses so leaves booked further out than the calendar window still
 * show. `type` allows the `'hidden'` privacy sentinel for calendar-sourced rows.
 */
export interface UpcomingLeave {
  id: string
  type: LeaveTypeId | 'hidden'
  startDate: string
  endDate: string
  halfDay: boolean
  status: LeaveStatus
  /** Server-computed billable days (weekends, public holidays and half-days
   *  excluded — the stored `chargeable_days`). Absent → weekday fallback. */
  days?: number | null | undefined
}

export interface UpcomingLeaveListProps {
  leaves: ReadonlyArray<UpcomingLeave>
  todayISO: string
  /** Max rows before "View all" takes over. Keeps the home surface compact. */
  limit?: number
}

/** Parse a `YYYY-MM-DD` string as *local* midnight (avoids UTC off-by-one). */
function localDate(iso: string): Date {
  return new Date(`${iso}T00:00:00`)
}

/** `YYYY-MM-DD` → `dd/mm/yyyy`. */
function fmtDMY(iso: string): string {
  return localDate(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/** "15 Jul 2026" (single day) · "13 Jul 2026 - 17 Jul 2026" (range). */
export function formatRange(startISO: string, endISO: string): string {
  if (startISO === endISO) return fmtDMY(startISO)
  return `${fmtDMY(startISO)} - ${fmtDMY(endISO)}`
}

/**
 * Leave length in days. Prefers the server count (`days`), which excludes
 * weekends, public holidays and half-days exactly as the balance does; the web
 * has no holiday list, so without it we fall back to counting weekdays.
 * "½ day" for a half-day.
 */
export function durationLabel(
  startISO: string,
  endISO: string,
  halfDay: boolean,
  days?: number | null,
): string {
  if (days != null) return dayCountLabel(days)
  if (halfDay && startISO === endISO) return '½ day'
  return dayCountLabel(countWeekdays(startISO, endISO))
}

function dayCountLabel(n: number): string {
  if (n === 0.5) return '½ day'
  if (n === 1) return '1 day'
  const shown = Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '')
  return `${shown} days`
}

/** Weekdays (Mon–Fri) between two dates, inclusive. */
function countWeekdays(startISO: string, endISO: string): number {
  let count = 0
  const end = localDate(endISO).getTime()
  for (let d = localDate(startISO); d.getTime() <= end; d.setDate(d.getDate() + 1)) {
    const dow = d.getDay()
    if (dow !== 0 && dow !== 6) count += 1
  }
  return count
}

/** Forward-looking cue: "Ongoing" (covers start-today) / "Tomorrow" / "in N days". */
export function whenLabel(startISO: string, endISO: string, todayISO: string): string {
  if (startISO <= todayISO && endISO >= todayISO) return 'Ongoing'
  const days = Math.round(
    (localDate(startISO).getTime() - localDate(todayISO).getTime()) / 86_400_000,
  )
  return days === 1 ? 'Tomorrow' : `in ${days} days`
}

function typeLabel(type: UpcomingLeave['type']): string {
  if (type === 'hidden') return 'Leave'
  return LEAVE_TYPE_LABEL[type as LeaveTypeId] ?? 'Leave'
}

function typeColor(type: UpcomingLeave['type']): string {
  if (type === 'hidden') return 'var(--text-3)'
  return LEAVE_TYPE_ACCENT[type as LeaveTypeId] ?? 'var(--text-3)'
}

/**
 * Forward-looking, date-sorted view of the viewer's leaves: drops anything
 * that has fully ended, sorts by start date. Pure so it's unit-testable
 * without a router context.
 */
export function selectUpcoming(
  leaves: ReadonlyArray<UpcomingLeave>,
  todayISO: string,
): ReadonlyArray<UpcomingLeave> {
  return leaves
    .filter((l) => l.endDate >= todayISO)
    .slice()
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
}

export function UpcomingLeaveList({
  leaves,
  todayISO,
  limit = 4,
}: UpcomingLeaveListProps): JSX.Element {
  const upcoming = selectUpcoming(leaves, todayISO)

  const shown = upcoming.slice(0, limit)
  const overflow = upcoming.length - shown.length

  return (
    <div
      data-testid="upcoming-leave-list"
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        padding: '16px 16px 12px',
        fontFamily: 'var(--font-sans)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: shown.length > 0 ? 12 : 8,
        }}
      >
        <div
          style={{
            fontSize: 10.5,
            color: 'var(--text-3)',
            fontFamily: 'var(--font-mono)',
            textTransform: 'uppercase',
            letterSpacing: '.08em',
            fontWeight: 700,
          }}
        >
          Upcoming leave
        </div>
        <Link
          to="/my-leave"
          style={{
            fontSize: 12,
            color: 'var(--text-3)',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          View all
          <Icon name="chevron_right" size={14} />
        </Link>
      </div>

      {shown.length === 0 ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 2px 6px',
            color: 'var(--text-3)',
            fontSize: 13,
          }}
        >
          <Icon name="event_available" size={18} />
          No upcoming leave booked.
          <Link
            to="/apply"
            style={{ color: 'var(--accent, var(--text-2))', textDecoration: 'none', fontWeight: 600 }}
          >
            Apply for leave
          </Link>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {shown.map((leave, i) => (
            <UpcomingLeaveRow
              key={leave.id}
              leave={leave}
              todayISO={todayISO}
              isLast={i === shown.length - 1}
            />
          ))}
          {overflow > 0 ? (
            <Link
              to="/my-leave"
              style={{
                fontSize: 12,
                color: 'var(--text-3)',
                textDecoration: 'none',
                textAlign: 'center',
                padding: '6px 0 2px',
              }}
            >
              +{overflow} more
            </Link>
          ) : null}
        </div>
      )}
    </div>
  )
}

const ROW_STYLE_BASE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 8px',
  borderRadius: 8,
  textDecoration: 'none',
  color: 'inherit',
  minHeight: 44,
}

// Status chip colour: green when approved, amber for anything still pending
// (matches the requested Approved/Pending semantics on this surface).
function statusVariant(status: UpcomingLeave['status']): 'success' | 'warning' {
  return status === 'approved' ? 'success' : 'warning'
}

/** The shared visible content of a row: dates (hero) + type chip + duration,
 *  then the "when" cue + status chip on the right. */
function RowInner({
  leave,
  todayISO,
}: {
  leave: UpcomingLeave
  todayISO: string
}): JSX.Element {
  // The home list is always the requester's OWN leaves, so the HR-review
  // holding state reads as plain "Pending" (display-only) — the owner is the
  // employee here, even if they're also HR.
  const shownStatus = ownerFacingStatus(leave.status)
  const status = STATUS_META[shownStatus]
  return (
    <>
      <div style={{ minWidth: 0, flex: 1 }}>
        {/* Hero — the dates */}
        <div
          style={{
            fontSize: 11.5,
            fontWeight: 600,
            color: 'var(--text)',
            letterSpacing: '-.01em',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {formatRange(leave.startDate, leave.endDate)}
        </div>
        {/* Meta — type chip + duration + view-details link */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '2px 8px',
              borderRadius: 999,
              background: 'var(--surface-2)',
              border: '1px solid var(--border)',
              fontSize: 11,
              fontWeight: 600,
              color: 'var(--text-2)',
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: typeColor(leave.type),
                flexShrink: 0,
              }}
            />
            {typeLabel(leave.type)}
          </span>
          <span style={{ fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
            {durationLabel(leave.startDate, leave.endDate, leave.halfDay, leave.days)}
          </span>
        </div>
      </div>
      <span
        style={{ fontSize: 11.5, color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0 }}
      >
        {whenLabel(leave.startDate, leave.endDate, todayISO)}
      </span>
      <Badge variant={statusVariant(shownStatus)} dot>
        {status.label}
      </Badge>
    </>
  )
}

/**
 * One row. Clicking the row opens the leave detail (`/leave?id`). Half-day-
 * capable leave types (annual / medical / unpaid / replacement) additionally
 * carry a caret that expands the row in place to reveal a per-day full/half
 * breakdown — the caret owns the expand, so it doesn't steal the row's
 * open-details gesture. Elapsed-day types (and the privacy 'hidden' sentinel)
 * are not expandable — their per-day counting rules differ, so no caret.
 */
function UpcomingLeaveRow({
  leave,
  todayISO,
  isLast,
}: {
  leave: UpcomingLeave
  todayISO: string
  isLast: boolean
}): JSX.Element {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const expandable =
    leave.type !== 'hidden' && LEAVE_TYPE_HALF_DAY_ALLOWED[leave.type as LeaveTypeId]
  // The row divider sits between rows. When a row is expanded, it moves from
  // under the header down to the bottom of the whole expanded block, so the
  // header stays visually attached to its own breakdown (no double border).
  const rowDivider = isLast ? 'none' : '1px solid var(--border)'

  const openDetails = (): void => {
    void navigate({ to: '/leave', search: { id: leave.id } })
  }

  const rowButton = (
    <div
      role="button"
      tabIndex={0}
      data-testid="upcoming-leave-row"
      className="upcoming-row"
      aria-label="View leave details"
      onClick={openDetails}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          openDetails()
        }
      }}
      style={{
        ...ROW_STYLE_BASE,
        borderBottom: open ? 'none' : rowDivider,
        cursor: 'pointer',
      }}
    >
      <RowInner leave={leave} todayISO={todayISO} />
      {expandable ? (
        <button
          type="button"
          data-testid="upcoming-leave-caret"
          aria-label={open ? 'Hide day breakdown' : 'Show day breakdown'}
          aria-expanded={open}
          // The caret owns the expand; stop the click reaching the row's
          // open-details handler.
          onClick={(e) => {
            e.stopPropagation()
            setOpen((o) => !o)
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: 'var(--text-3)',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'transform var(--motion-duration-base, 150ms) ease',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        >
          <Icon name="expand_more" size={18} />
        </button>
      ) : null}
    </div>
  )

  if (!expandable) {
    return <div style={{ borderBottom: rowDivider }}>{rowButton}</div>
  }

  return (
    <div style={{ borderBottom: open ? rowDivider : 'none' }}>
      {rowButton}
      <AnimatedHeight duration="base">
        {open ? <LeaveDayBreakdown leaveId={leave.id} /> : null}
      </AnimatedHeight>
    </div>
  )
}
